import { request } from "./client";

/**
 * Search games by league city and state.
 * @param {{ city: string, state: string }} params
 */
export const searchGames = async (params) => {
  const searchParams = new URLSearchParams();
  searchParams.set("city", params.city.trim());
  searchParams.set("state", params.state.trim());
  return request(`/games?${searchParams.toString()}`);
};

/**
 * Create a game for a league. Capacity (max skaters/goalies) is determined by the league.
 * @param {{ leagueId: string, rinkId?: string, startsAt: string | Date, durationMinutes: number, arenaName?: string }} payload
 */
export const createGame = async (payload) => {
  return request("/games", {
    method: "POST",
    body: JSON.stringify({
      leagueId: payload.leagueId,
      rinkId: payload.rinkId,
      startsAt: payload.startsAt,
      durationMinutes: payload.durationMinutes,
      arenaName: payload.arenaName,
    }),
  });
};

/**
 * Get a single game with full roster details.
 * @param {string} gameId
 */
export const getGame = async (gameId) => {
  return request(`/games/${gameId}`);
};

/**
 * Register for a game as a skater or goalie.
 * @param {string} gameId
 * @param {{ email: string, firstName: string, lastName: string, role: "SKATER"|"GOALIE", preferredPosition?: "CENTER"|"WING"|"DEFENSE", payment?: { amountCents: number, paymentNote?: string } }} payload
 */
export const registerForGame = async (gameId, payload) => {
  return request(`/games/${gameId}/registrations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

/**
 * Update a game's rink, start time, or duration.
 * @param {string} gameId
 * @param {{ rinkId?: string|null, startsAt?: string, durationMinutes?: number, arenaName?: string|null }} payload
 */
export const updateGame = async (gameId, payload) => {
  return request(`/games/${gameId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
};

/**
 * Get all game registrations for the current signed-in user.
 */
export const getMyGames = async () => {
  return request("/games/me");
};

/**
 * Cancel a game registration.
 * @param {string} gameId
 * @param {string} registrationId
 */
export const cancelRegistration = async (gameId, registrationId) => {
  return request(`/games/${gameId}/registrations/${registrationId}`, {
    method: "DELETE",
  });
};

/**
 * Get all games with registrations for a league (organizer only).
 * @param {string} leagueId
 */
export const getLeagueRegistrationsForCheckoff = async (leagueId) => {
  return request(`/games/leagues/${leagueId}/registrations`);
};

/**
 * Cancel a game (marks it CANCELLED, preserves registrations).
 * @param {string} gameId
 */
export const cancelGame = async (gameId) => {
  return request(`/games/${gameId}/cancel`, { method: "PATCH" });
};

/**
 * Permanently delete a game. Only succeeds if the game has no registrations.
 * @param {string} gameId
 */
export const deleteGame = async (gameId) => {
  return request(`/games/${gameId}`, { method: "DELETE" });
};

/**
 * Update the hasPaid status of a game registration.
 * @param {string} registrationId
 * @param {boolean} hasPaid
 */
export const updateRegistrationPaid = async (registrationId, hasPaid) => {
  return request(`/games/registrations/${registrationId}/paid`, {
    method: "PATCH",
    body: JSON.stringify({ hasPaid }),
  });
};

/**
 * Update the RSVP status of the current user's game registration.
 * @param {string} registrationId
 * @param {"YES"|"NO"|"NO_RESPONSE"} rsvp
 */
export const updateRegistrationRsvp = async (registrationId, rsvp) => {
  return request(`/games/registrations/${registrationId}/rsvp`, {
    method: "PATCH",
    body: JSON.stringify({ rsvp }),
  });
};
